import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Abmelden — PIXIWEB" },
      { name: "description", content: "Newsletter und Benachrichtigungen von PIXIWEB jederzeit mit einem Klick abbestellen — DSGVO-konform und ohne Anmeldung." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Abmelden — PIXIWEB" },
      { property: "og:description", content: "Newsletter und Benachrichtigungen von PIXIWEB jederzeit mit einem Klick abbestellen — DSGVO-konform und ohne Anmeldung." },
      { property: "og:url", content: "https://pixiweb.de/unsubscribe" },
    ],
    links: [{ rel: "canonical", href: "https://pixiweb.de/unsubscribe" }],
  }),
});

function UnsubscribePage() {
  const [state, setState] = useState<"loading" | "ready" | "already" | "invalid" | "done" | "error">("loading");
  const [token, setToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token");
    setToken(t);
    if (!t) { setState("invalid"); return; }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) { setState("invalid"); return; }
        if (d.valid) setState("ready");
        else if (d.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("error"));
  }, []);

  async function confirm() {
    if (!token) return;
    setSubmitting(true);
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) setState("done");
      else if (d.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ice px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-mist bg-white p-8 text-center shadow-pixi">
        <h1 className="font-display text-2xl font-bold text-navy">E-Mail abmelden</h1>
        {state === "loading" && <p className="mt-4 text-sm text-slate">Lade…</p>}
        {state === "ready" && (
          <>
            <p className="mt-3 text-sm text-slate">
              Möchtest du dich von zukünftigen E-Mails von PIXIWEB abmelden?
            </p>
            <button
              onClick={confirm}
              disabled={submitting}
              className="mt-6 rounded-xl bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-electric disabled:opacity-60"
            >
              {submitting ? "Bitte warten…" : "Abmelden bestätigen"}
            </button>
          </>
        )}
        {state === "done" && (
          <p className="mt-4 text-sm text-slate">
            Du wurdest erfolgreich abgemeldet. Du erhältst keine weiteren E-Mails.
          </p>
        )}
        {state === "already" && (
          <p className="mt-4 text-sm text-slate">Diese E-Mail-Adresse ist bereits abgemeldet.</p>
        )}
        {state === "invalid" && (
          <p className="mt-4 text-sm text-slate">Dieser Abmelde-Link ist ungültig oder abgelaufen.</p>
        )}
        {state === "error" && (
          <p className="mt-4 text-sm text-red-600">Etwas ist schiefgelaufen. Bitte versuche es erneut.</p>
        )}
      </div>
    </div>
  );
}
